import { useCallback, useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { ShieldAlert, Snowflake, Sun, Search } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { planLabel, formatNumber } from "@/lib/plans";
import AdminCatalog from "@/pages/AdminCatalog";

// Admin: people and their usage, the freeze switch, scraping alerts,
// sign-up drop-offs, cancellation and uninstall feedback, and plan limits.
// Every call is an admin_* RPC (migration 031) that refuses non-admins, so
// hiding this page is a convenience, not the protection.
//
// The Catalog tab is the raw ClickHouse catalog (AdminCatalog.jsx). Its
// endpoints on the search API also demand an authenticator code (aal2).

const ago = (d) => (d ? formatDistanceToNow(new Date(d), { addSuffix: true }) : "—");

export default function Admin() {
  const { user } = useAuth();
  if (user?.role !== "admin") {
    return (
      <div className="grid h-full place-items-center p-10 text-center" data-testid="admin-denied">
        <div>
          <ShieldAlert className="mx-auto h-8 w-8 text-muted-foreground" />
          <h1 className="mt-3 font-display text-xl">Admins only</h1>
          <p className="mt-1 text-sm text-muted-foreground">Ask an Inbound Labs admin if you need access.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="border-b border-border px-6 py-4 md:px-10">
        <div className="eyebrow">Internal</div>
        <h1 className="font-display text-2xl tracking-tight">Admin</h1>
      </header>
      <div className="flex-1 overflow-y-auto px-6 py-6 md:px-10">
        <Tabs defaultValue="users">
          <TabsList>
            <TabsTrigger value="users" data-testid="admin-tab-users">People</TabsTrigger>
            <TabsTrigger value="alerts" data-testid="admin-tab-alerts">Alerts</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Sign-up drop-offs</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="admin-tab-feedback">Feedback</TabsTrigger>
            <TabsTrigger value="limits" data-testid="admin-tab-limits">Limits</TabsTrigger>
            <TabsTrigger value="catalog" data-testid="admin-tab-catalog">Catalog</TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="mt-6"><Users /></TabsContent>
          <TabsContent value="alerts" className="mt-6"><Alerts /></TabsContent>
          <TabsContent value="leads" className="mt-6"><Leads /></TabsContent>
          <TabsContent value="feedback" className="mt-6"><Feedback /></TabsContent>
          <TabsContent value="limits" className="mt-6"><Limits /></TabsContent>
          {/* Mounted only when opened, so the authenticator step starts on demand. */}
          <TabsContent value="catalog" className="mt-6"><AdminCatalog /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function useRpc(name, args) {
  const [data, setData] = useState(null);
  const key = JSON.stringify(args || {});
  const load = useCallback(async () => {
    const { data: d, error } = await supabase.rpc(name, JSON.parse(key));
    if (error) { toast.error(error.message); setData([]); return; }
    setData(d ?? []);
  }, [name, key]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

function Table({ head, children, testid }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-card" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-surface-inset text-left text-xs font-medium text-muted-foreground">
            {head.map((h) => <th key={h} className="whitespace-nowrap px-3 py-2.5">{h}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

const Empty = ({ children }) => (
  <tr><td colSpan={20} className="px-3 py-10 text-center text-muted-foreground">{children}</td></tr>
);

function FreezeDialog({ target, onClose, onDone }) {
  const [reason, setReason] = useState("");
  useEffect(() => { setReason(""); }, [target?.id]);
  const freeze = async () => {
    const { data, error } = await supabase.rpc("admin_set_account_status", {
      p_user_id: target.id, p_status: "frozen", p_reason: reason || null,
    });
    if (error || data?.error) { toast.error(error?.message || "Couldn't freeze that account."); return; }
    toast.success(`Froze ${target.email}`);
    onDone();
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Freeze {target?.email}?</DialogTitle>
          <DialogDescription>
            They can still sign in and search, but reveals, Enrich and exports stop until you unfreeze them.
          </DialogDescription>
        </DialogHeader>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (shown only here)" data-testid="freeze-reason" />
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={freeze} data-testid="freeze-confirm">Freeze account</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Users() {
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [rows, reload] = useRpc("admin_list_users", { p_search: search || null });
  const [freezing, setFreezing] = useState(null);

  const unfreeze = async (u) => {
    const { error } = await supabase.rpc("admin_set_account_status", { p_user_id: u.id, p_status: "active", p_reason: null });
    if (error) { toast.error(error.message); return; }
    toast.success(`Unfroze ${u.email}`);
    reload();
  };

  return (
    <div className="space-y-4">
      <form onSubmit={(e) => { e.preventDefault(); setSearch(q.trim()); }} className="flex max-w-md gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email, name or company" className="pl-8" data-testid="admin-user-search" />
        </div>
        <Button type="submit" variant="outline">Search</Button>
      </form>
      <Table testid="admin-users-table" head={["Person", "Plan", "Credits left", "Reveals today", "Alerts", "Flags", "Joined", "Status", ""]}>
        {rows === null ? <Empty>Loading…</Empty> : rows.length === 0 ? <Empty>No one matches.</Empty> : rows.map((u) => (
          <tr key={u.id} className="border-b border-border/60" data-testid={`admin-user-${u.email}`}>
            <td className="px-3 py-2.5">
              <div className="font-medium">{u.email}</div>
              <div className="text-xs text-muted-foreground">{[u.name, u.company].filter(Boolean).join(" · ") || "—"}</div>
            </td>
            <td className="px-3 py-2.5">{planLabel(u.plan)}</td>
            <td className="px-3 py-2.5 tabular-nums">{formatNumber(u.credits_total - u.credits_used)}</td>
            <td className={`px-3 py-2.5 tabular-nums ${u.reveals_today >= u.daily_limit ? "font-semibold text-warning" : ""}`}>
              {formatNumber(u.reveals_today)} / {formatNumber(u.daily_limit)}
            </td>
            <td className="px-3 py-2.5">{u.open_alerts > 0 ? <Badge variant="destructive">{u.open_alerts}</Badge> : "—"}</td>
            <td className="px-3 py-2.5 text-xs">
              {u.abuse_flag === "duplicate_email" ? <Badge variant="outline">Repeat email, no free credits</Badge> : "—"}
              {u.signup_source === "extension" && <div className="mt-1 text-muted-foreground">via extension</div>}
            </td>
            <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">{ago(u.created_at)}</td>
            <td className="px-3 py-2.5">
              {u.account_status === "frozen"
                ? <Badge variant="destructive" title={u.frozen_reason || ""}>Frozen</Badge>
                : <Badge variant="secondary">Active</Badge>}
            </td>
            <td className="px-3 py-2.5 text-right">
              {u.account_status === "frozen" ? (
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => unfreeze(u)} data-testid={`unfreeze-${u.email}`}>
                  <Sun className="h-3.5 w-3.5" /> Unfreeze
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="gap-1.5 text-destructive" onClick={() => setFreezing(u)} data-testid={`freeze-${u.email}`}>
                  <Snowflake className="h-3.5 w-3.5" /> Freeze
                </Button>
              )}
            </td>
          </tr>
        ))}
      </Table>
      <FreezeDialog target={freezing} onClose={() => setFreezing(null)} onDone={() => { setFreezing(null); reload(); }} />
    </div>
  );
}

const ALERT_TEXT = {
  fast_reveals: (d) => `${formatNumber(d.reveals_last_hour)} reveals in one hour (daily limit ${formatNumber(d.daily_limit)})`,
  big_enrich: (d) => `Enrich upload of ${formatNumber(d.rows)} rows (limit ${formatNumber(d.max_rows)})`,
};

function Alerts() {
  const [rows, reload] = useRpc("admin_list_alerts", { p_include_resolved: false });
  const [freezing, setFreezing] = useState(null);
  const dismiss = async (a) => {
    const { error } = await supabase.rpc("admin_resolve_alert", { p_alert_id: a.id });
    if (error) { toast.error(error.message); return; }
    reload();
  };
  return (
    <div className="space-y-4">
      <p className="max-w-2xl text-sm text-muted-foreground">
        Raised automatically when someone reveals 80% of their daily limit within an hour, or uploads a very large Enrich file.
      </p>
      <Table testid="admin-alerts-table" head={["Person", "What happened", "When", "Status", ""]}>
        {rows === null ? <Empty>Loading…</Empty> : rows.length === 0 ? <Empty>No open alerts.</Empty> : rows.map((a) => (
          <tr key={a.id} className="border-b border-border/60">
            <td className="px-3 py-2.5 font-medium">{a.email}</td>
            <td className="px-3 py-2.5">{(ALERT_TEXT[a.kind] || (() => a.kind))(a.detail || {})}</td>
            <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">{ago(a.created_at)}</td>
            <td className="px-3 py-2.5">{a.account_status === "frozen" ? <Badge variant="destructive">Frozen</Badge> : <Badge variant="secondary">Active</Badge>}</td>
            <td className="space-x-2 whitespace-nowrap px-3 py-2.5 text-right">
              {a.account_status !== "frozen" && (
                <Button size="sm" variant="outline" className="text-destructive" onClick={() => setFreezing({ id: a.user_id, email: a.email })}>Freeze</Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => dismiss(a)} data-testid={`dismiss-alert-${a.id}`}>Dismiss</Button>
            </td>
          </tr>
        ))}
      </Table>
      <FreezeDialog target={freezing} onClose={() => setFreezing(null)} onDone={() => { setFreezing(null); reload(); }} />
    </div>
  );
}

const STEP_LABEL = { email: "Gave email", name: "Gave name", company: "Gave company", password: "At password", verify: "Waiting for code", account_created: "Signed up" };

function Leads() {
  const [onlyDrops, setOnlyDrops] = useState(true);
  const [rows] = useRpc("admin_list_signup_leads", { p_only_dropoffs: onlyDrops });
  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <Switch checked={onlyDrops} onCheckedChange={setOnlyDrops} data-testid="leads-only-dropoffs" /> Only people who didn't finish
      </label>
      <Table testid="admin-leads-table" head={["Email", "Name", "Company", "Got to", "Came from", "Started"]}>
        {rows === null ? <Empty>Loading…</Empty> : rows.length === 0 ? <Empty>No sign-ups to show.</Empty> : rows.map((l) => (
          <tr key={l.id} className="border-b border-border/60">
            <td className="px-3 py-2.5 font-medium">{l.email || "—"}</td>
            <td className="px-3 py-2.5">{l.name || "—"}</td>
            <td className="px-3 py-2.5">{l.company || "—"}</td>
            <td className="px-3 py-2.5">{l.converted_user_id ? <Badge variant="secondary">Signed up</Badge> : STEP_LABEL[l.step] || l.step}</td>
            <td className="px-3 py-2.5 text-xs text-muted-foreground">
              {[l.source, l.plan && `plan: ${l.plan}`, l.utm?.utm_source && `utm: ${l.utm.utm_source}`].filter(Boolean).join(" · ") || "—"}
            </td>
            <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">{ago(l.created_at)}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function Feedback() {
  const [data] = useRpc("admin_list_feedback");
  const cancels = data?.cancellations || [];
  const uninstalls = data?.uninstalls || [];
  const sales = data?.sales || [];
  return (
    <div className="grid gap-8">
      <section className="space-y-3">
        <h2 className="font-display text-lg">Sales requests</h2>
        <Table testid="admin-sales-table" head={["Person", "Company", "Team", "Credits a month", "Looking for", "When"]}>
          {data === null ? <Empty>Loading…</Empty> : sales.length === 0 ? <Empty>No sales requests yet.</Empty> : sales.map((r) => (
            <tr key={r.id} className="border-b border-border/60">
              <td className="px-3 py-2.5">
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-muted-foreground">{[r.email, r.phone].filter(Boolean).join(" · ")}</div>
              </td>
              <td className="px-3 py-2.5">{r.company}</td>
              <td className="px-3 py-2.5">{r.team_size || "—"}</td>
              <td className="px-3 py-2.5">{r.monthly_volume || "—"}</td>
              <td className="max-w-sm px-3 py-2.5 text-muted-foreground">{r.needs}</td>
              <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">{ago(r.created_at)}</td>
            </tr>
          ))}
        </Table>
      </section>
      <section className="space-y-3">
        <h2 className="font-display text-lg">Cancellations</h2>
        <Table testid="admin-cancellations-table" head={["Person", "Plan", "Reason", "Details", "Outcome", "When"]}>
          {data === null ? <Empty>Loading…</Empty> : cancels.length === 0 ? <Empty>No cancellations yet.</Empty> : cancels.map((c) => (
            <tr key={c.id} className="border-b border-border/60">
              <td className="px-3 py-2.5 font-medium">{c.email}</td>
              <td className="px-3 py-2.5">{planLabel(c.plan)}</td>
              <td className="px-3 py-2.5">{c.reason.replace(/_/g, " ")}</td>
              <td className="max-w-xs px-3 py-2.5 text-muted-foreground">{c.details || "—"}</td>
              <td className="px-3 py-2.5">{c.outcome === "kept" ? <Badge variant="secondary">Stayed</Badge> : <Badge variant="outline">Cancelled</Badge>}</td>
              <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">{ago(c.created_at)}</td>
            </tr>
          ))}
        </Table>
      </section>
      <section className="space-y-3">
        <h2 className="font-display text-lg">Extension uninstalls</h2>
        <Table testid="admin-uninstalls-table" head={["Reason", "Details", "Email", "When"]}>
          {data === null ? <Empty>Loading…</Empty> : uninstalls.length === 0 ? <Empty>No uninstall answers yet.</Empty> : uninstalls.map((f) => (
            <tr key={f.id} className="border-b border-border/60">
              <td className="px-3 py-2.5">{f.reason.replace(/_/g, " ")}</td>
              <td className="max-w-xs px-3 py-2.5 text-muted-foreground">{f.details || "—"}</td>
              <td className="px-3 py-2.5">{f.email || "—"}</td>
              <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">{ago(f.created_at)}</td>
            </tr>
          ))}
        </Table>
      </section>
    </div>
  );
}

function Limits() {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    const { data, error } = await supabase.from("plan_limits")
      .select("plan, label, monthly_credits, daily_reveal_limit, max_rows_per_job, sort_order")
      .order("sort_order");
    if (error) { toast.error(error.message); setRows([]); return; }
    setRows(data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (r) => {
    const { data, error } = await supabase.rpc("admin_update_plan_limits", {
      p_plan: r.plan, p_daily_reveal_limit: Number(r.daily_reveal_limit), p_max_rows_per_job: Number(r.max_rows_per_job),
    });
    if (error || data?.error) { toast.error(error?.message || "Enter whole numbers above zero."); return; }
    toast.success(`${r.label} limits saved`);
  };
  const set = (plan, k, v) => setRows((prev) => prev.map((r) => (r.plan === plan ? { ...r, [k]: v.replace(/\D/g, "") } : r)));

  return (
    <div className="space-y-4">
      <p className="max-w-2xl text-sm text-muted-foreground">
        Changes apply immediately to everyone on that plan. Search is never limited.
      </p>
      <Table testid="admin-limits-table" head={["Plan", "Credits a month", "Reveals a day", "Rows per upload or export", ""]}>
        {rows === null ? <Empty>Loading…</Empty> : rows.map((r) => (
          <tr key={r.plan} className="border-b border-border/60">
            <td className="px-3 py-2.5 font-medium">{r.label}</td>
            <td className="px-3 py-2.5 tabular-nums text-muted-foreground">{formatNumber(r.monthly_credits)}</td>
            <td className="px-3 py-2.5"><Input className="h-9 w-28 tabular-nums" inputMode="numeric" value={String(r.daily_reveal_limit)}
              onChange={(e) => set(r.plan, "daily_reveal_limit", e.target.value)} data-testid={`limit-daily-${r.plan}`} aria-label={`${r.label} reveals a day`} /></td>
            <td className="px-3 py-2.5"><Input className="h-9 w-28 tabular-nums" inputMode="numeric" value={String(r.max_rows_per_job)}
              onChange={(e) => set(r.plan, "max_rows_per_job", e.target.value)} data-testid={`limit-rows-${r.plan}`} aria-label={`${r.label} rows per upload`} /></td>
            <td className="px-3 py-2.5 text-right"><Button size="sm" onClick={() => save(r)} data-testid={`limit-save-${r.plan}`}>Save</Button></td>
          </tr>
        ))}
      </Table>
    </div>
  );
}
